import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  notificationsApi,
  onboardingApi,
  peopleApi,
  rolesApi,
  settingsApi,
  vehicleTypesApi,
  type CompanyProfile,
  type HardFloor,
  type NotificationTemplate,
  type NotificationTier,
  type Role,
  type SettingRow,
  type VehicleType,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal, ConfirmModal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useConfirm, useToast } from '@/components/Feedback';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';
import { formatDateTime } from '@/lib/format';
import {
  LOCKOUT_PERMISSIONS,
  SETTING_META,
  channelLabel,
  deliveryResultLabel,
  groupPermissions,
  notificationTierLabel,
  notificationTypeLabel,
  permissionLabel,
  settingDomainLabel,
  settingLabel,
} from '@/lib/labels';

type Tab = 'company' | 'vehicle-types' | 'registry' | 'roles' | 'notifications';

const TAB_VALUES: Tab[] = ['company', 'vehicle-types', 'registry', 'roles', 'notifications'];

export function AdminSettings() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const tab = (TAB_VALUES.includes(rawTab as Tab) ? rawTab : 'company') as Tab;

  const setTab = (next: Tab) => {
    if (next === 'company') setParams({});
    else setParams({ tab: next });
  };

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <p className="kicker">Settings</p>
          <h1>Settings</h1>
          <p className="text-muted">
            Company profile and booking rules, vehicle types, advanced settings, user roles, and notifications
          </p>
        </div>

        <div className="settings-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={tab === 'company' ? 'active' : ''}
            aria-selected={tab === 'company'}
            onClick={() => setTab('company')}
          >
            Company profile
          </button>
          <button
            type="button"
            role="tab"
            className={tab === 'vehicle-types' ? 'active' : ''}
            aria-selected={tab === 'vehicle-types'}
            onClick={() => setTab('vehicle-types')}
          >
            Vehicle types
          </button>
          <button
            type="button"
            role="tab"
            className={tab === 'registry' ? 'active' : ''}
            aria-selected={tab === 'registry'}
            onClick={() => setTab('registry')}
          >
            Advanced
          </button>
          <button
            type="button"
            role="tab"
            className={tab === 'roles' ? 'active' : ''}
            aria-selected={tab === 'roles'}
            onClick={() => setTab('roles')}
          >
            Roles
          </button>
          <button
            type="button"
            role="tab"
            className={tab === 'notifications' ? 'active' : ''}
            aria-selected={tab === 'notifications'}
            onClick={() => setTab('notifications')}
          >
            Notifications
          </button>
        </div>

        {tab === 'company' && (
          <>
            <CompanyProfilePanel />
            <BookingRulesPanel />
          </>
        )}
        {tab === 'vehicle-types' && <VehicleTypesPanel />}
        {tab === 'registry' && <SettingsRegistryPanel />}
        {tab === 'roles' && <RolesPanel />}
        {tab === 'notifications' && <NotificationsPanel />}

        <style>{`
          .settings-tabs {
            display: flex;
            gap: 2px;
            margin-bottom: var(--space-lg);
            border-bottom: 1px solid var(--color-border);
          }
          .settings-tabs button {
            background: none;
            border: none;
            padding: var(--space-sm) var(--space-lg);
            font-weight: 600;
            font-size: 0.875rem;
            color: var(--color-steel);
            cursor: pointer;
            border-bottom: 2px solid transparent;
            margin-bottom: -1px;
          }
          .settings-tabs button.active {
            color: var(--color-ink);
            border-bottom-color: var(--color-signal);
          }
        `}</style>
      </div>
    </Layout>
  );
}

function CompanyProfilePanel() {
  const { data: profile, loading, reload } = useAsync(() => settingsApi.getCompany(), []);
  const [form, setForm] = useState<CompanyProfile>({
    legalName: '',
    tradingName: '',
    tin: '',
    billingAddress: '',
    hubLatitude: 14.5995,
    hubLongitude: 120.9842,
    timezone: 'Asia/Manila',
    currency: 'PHP',
  });
  const [saving, setSaving] = useState(false);
  const { flash, ok, error, clear } = useFlash();
  const toast = useToast();

  useEffect(() => {
    if (profile) setForm((prev) => ({ ...prev, ...profile }));
  }, [profile]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clear();
    try {
      await settingsApi.updateCompany({
        legalName: form.legalName,
        tradingName: form.tradingName,
        tin: form.tin,
        billingAddress: form.billingAddress,
        hubLatitude: Number(form.hubLatitude),
        hubLongitude: Number(form.hubLongitude),
      });
      ok('Company profile saved');
      toast('Company profile saved');
      reload();
    } catch (err) {
      error(errorText(err, 'Could not save the company profile.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-center"><div className="spinner" /></div>;
  }

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <h2>Company profile</h2>
      <p className="text-muted" style={{ marginTop: '-0.5rem' }}>
        Legal details and hub location — needed before documents and dispatch
      </p>
      <Input
        label="Legal name"
        value={form.legalName}
        onChange={(e) => setForm({ ...form, legalName: e.target.value })}
        placeholder="11Eleven Logistics Inc."
        required
      />
      <Input
        label="Trading name"
        value={form.tradingName}
        onChange={(e) => setForm({ ...form, tradingName: e.target.value })}
        placeholder="11Eleven Logistics"
      />
      <Input
        label="TIN"
        value={form.tin}
        onChange={(e) => setForm({ ...form, tin: e.target.value })}
        placeholder="000-000-000-000"
      />
      <Input
        label="Billing address"
        value={form.billingAddress}
        onChange={(e) => setForm({ ...form, billingAddress: e.target.value })}
        placeholder="Metro Manila, Philippines"
      />
      <div className="grid-2">
        <Input
          label="Hub latitude"
          type="number"
          step="any"
          value={String(form.hubLatitude ?? '')}
          onChange={(e) => setForm({ ...form, hubLatitude: Number(e.target.value) })}
        />
        <Input
          label="Hub longitude"
          type="number"
          step="any"
          value={String(form.hubLongitude ?? '')}
          onChange={(e) => setForm({ ...form, hubLongitude: Number(e.target.value) })}
        />
      </div>
      <p className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '-0.5rem' }}>
        Easier: add your depot in the <Link to="/app/admin/addresses">Address book</Link> and choose
        “No client (internal hub)”. Its map location is found for you and it completes the hub step
        of the setup checklist.
      </p>
      <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
        Currency is fixed to PHP · Timezone Asia/Manila · VAT 12%
      </p>
      <FlashMessage flash={flash} />
      <Button type="submit" loading={saving}>Save company profile</Button>
      <style>{formStyles}</style>
    </form>
  );
}

function VehicleTypesPanel() {
  const { data: types, loading, reload } = useAsync(() => vehicleTypesApi.list(), []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<VehicleType | null>(null);
  const [form, setForm] = useState({
    code: '',
    name: '',
    payloadKg: '7000',
    tollClass: '2',
    kmPerLitre: '4.0',
    status: 'active',
  });
  const [saving, setSaving] = useState(false);
  const { flash, ok, error, clear } = useFlash();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState<VehicleType | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm({
      code: '',
      name: '',
      payloadKg: '7000',
      tollClass: '2',
      kmPerLitre: '4.0',
      status: 'active',
    });
    clear();
    setShowForm(true);
  };

  const openEdit = (row: VehicleType) => {
    setEditing(row);
    setForm({
      code: row.code,
      name: row.name,
      payloadKg: String(row.payloadKg),
      tollClass: String(row.tollClass),
      kmPerLitre: String(row.kmPerLitre),
      status: row.status,
    });
    clear();
    setShowForm(true);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clear();
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        payloadKg: Number(form.payloadKg),
        tollClass: Number(form.tollClass),
        kmPerLitre: form.kmPerLitre,
        status: form.status,
      };
      if (editing) {
        await vehicleTypesApi.update(editing.id, payload);
      } else {
        await vehicleTypesApi.create(payload);
      }
      setShowForm(false);
      toast(editing ? `Vehicle type ${payload.name} updated` : `Vehicle type ${payload.name} added`);
      reload();
    } catch (err) {
      error(errorText(err, 'Could not save the vehicle type.'));
    } finally {
      setSaving(false);
    }
  };

  const seed = async () => {
    setSaving(true);
    clear();
    try {
      await vehicleTypesApi.seed();
      ok('Standard types added (6-wheeler forward, 10-wheeler wing van)');
      reload();
    } catch (err) {
      error(errorText(err, 'Could not add the standard vehicle types.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    clear();
    try {
      await vehicleTypesApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      ok('Vehicle type deleted');
      reload();
    } catch (err) {
      error(errorText(err, 'Could not delete the vehicle type.'));
      setConfirmDelete(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="vt-panel">
      <div className="row-between" style={{ marginBottom: '1rem' }}>
        <div>
          <h2>Vehicle types</h2>
          <p className="text-muted">
            The truck sizes you run (for example 6-wheeler, 10-wheeler). Rate cards, pay cards and
            fleet vehicles are all set up per vehicle type.
          </p>
        </div>
        <div className="row">
          <Button size="sm" variant="secondary" loading={saving} onClick={seed}>
            Add standard types
          </Button>
          <Button size="sm" onClick={openAdd}>Add vehicle type</Button>
        </div>
      </div>

      <FlashMessage flash={flash} />

      <DataTable
        data={types ?? []}
        keyField="id"
        loading={loading}
        dense
        emptyMessage="No vehicle types yet. Click “Add standard types” for 6-wheeler and 10-wheeler, or add your own."
        columns={[
          { key: 'code', header: 'Code' },
          { key: 'name', header: 'Name' },
          {
            key: 'payloadKg',
            header: 'Payload kg',
            render: (r) => r.payloadKg.toLocaleString('en-PH'),
          },
          { key: 'tollClass', header: 'Toll class' },
          {
            key: 'kmPerLitre',
            header: 'km/L',
            render: (r) => <span className="data-num">{r.kmPerLitre}</span>,
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status} size="sm" />,
          },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <div className="row-actions">
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>Delete</Button>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit vehicle type' : 'Add vehicle type'}
      >
        <form onSubmit={save}>
          <Input
            label="Code"
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            required
            placeholder="6W_FWD"
            disabled={!!editing}
            hint={editing ? 'The code can’t be changed after the type is created' : 'Short code used in rate card spreadsheets, e.g. 6W'}
          />
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            placeholder="6-Wheeler Forward"
          />
          <div className="grid-2">
            <Input
              label="Payload (kg)"
              type="number"
              min="0"
              value={form.payloadKg}
              onChange={(e) => setForm({ ...form, payloadKg: e.target.value })}
              required
            />
            <Input
              label="Toll class"
              type="number"
              min="1"
              value={form.tollClass}
              onChange={(e) => setForm({ ...form, tollClass: e.target.value })}
              required
            />
          </div>
          <Input
            label="Fuel efficiency (km/L)"
            type="number"
            step="0.1"
            min="0"
            value={form.kmPerLitre}
            onChange={(e) => setForm({ ...form, kmPerLitre: e.target.value })}
            required
          />
          {editing && (
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          )}
          {showForm && <FlashMessage flash={flash} />}
          <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Create type'}</Button>
        </form>
        <style>{formStyles}</style>
      </Modal>

      <ConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={remove}
        title="Delete vehicle type"
        message={
          confirmDelete
            ? `Delete “${confirmDelete.name}”? Types used by vehicles or rate/pay cards must be deactivated instead.`
            : ''
        }
        confirmLabel="Delete"
        danger
        loading={saving}
      />

      <style>{`
        .vt-panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .vt-panel h2 { margin-bottom: var(--space-xs); }
        .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
        .data-num {
          font-family: var(--font-display);
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }
        ${formStyles}
      `}</style>
    </section>
  );
}

function BookingRulesPanel() {
  const { data: current, loading, error: loadError, reload } = useAsync(async () => {
    const rows = await settingsApi.list();
    const row = rows.find((r) => r.key === 'booking.min_lead_time_hours');
    return row?.value == null ? '' : String(row.value);
  }, []);
  const { data: review, reload: reloadReview } = useAsync(
    () => onboardingApi.dispatchSettingsReview(),
    [],
  );
  const [leadHours, setLeadHours] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const { flash, ok, error, clear } = useFlash();
  const toast = useToast();

  useEffect(() => {
    if (typeof current === 'string') setLeadHours(current);
  }, [current]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const hours = Number(leadHours);
    if (!Number.isFinite(hours) || hours < 0) {
      error('Enter the number of hours, e.g. 3.');
      return;
    }
    setSaving(true);
    clear();
    try {
      await settingsApi.setValue('booking.min_lead_time_hours', hours);
      await onboardingApi.confirmDispatchSettings();
      toast('Booking rules saved');
      ok(`Clients must book at least ${hours} hour${hours === 1 ? '' : 's'} ahead`);
      reload();
      reloadReview();
    } catch (err) {
      error(errorText(err, 'Could not save booking rules.'));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    clear();
    try {
      await onboardingApi.confirmDispatchSettings();
      toast('Booking rules confirmed');
      ok('Thanks — the current booking rules are confirmed.');
      reloadReview();
    } catch (err) {
      error(errorText(err, 'Could not confirm booking rules.'));
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return <div className="loading-center" style={{ marginTop: 'var(--space-lg)' }}><div className="spinner" /></div>;
  }

  return (
    <form className="admin-form booking-rules-panel" onSubmit={handleSubmit} id="booking-rules">
      <h2>Booking rules</h2>
      <p className="text-muted" style={{ marginTop: '-0.5rem' }}>
        Saving or confirming these rules completes the “Dispatch settings reviewed” step of the setup checklist.
      </p>
      {review?.reviewedAt ? (
        <p className="text-muted booking-rules-reviewed">
          Last confirmed {formatDateTime(review.reviewedAt)}
          {review.reviewedByName ? ` by ${review.reviewedByName}` : ''}.
        </p>
      ) : (
        <p className="booking-rules-reviewed">Not confirmed yet — check the value below, then save or confirm it.</p>
      )}
      {loadError && (
        <p className="text-urgent">
          Could not load the current value. {loadError}{' '}
          <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Retry</Button>
        </p>
      )}
      <Input
        label="Minimum booking lead time (hours)"
        type="number"
        inputMode="decimal"
        min="0"
        step="0.5"
        value={leadHours}
        onChange={(e) => setLeadHours(e.target.value)}
        hint="How far ahead clients must book a trip. Must be more than 0."
        required
      />
      <FlashMessage flash={flash} />
      <div className="row">
        <Button type="submit" loading={saving} disabled={confirming}>Save booking rules</Button>
        <Button
          type="button"
          variant="secondary"
          loading={confirming}
          disabled={saving || leadHours !== current}
          title={leadHours !== current ? 'You have unsaved changes — save them instead' : undefined}
          onClick={() => void handleConfirm()}
        >
          Confirm these settings
        </Button>
      </div>
      <style>{`${formStyles} .booking-rules-panel { margin-top: var(--space-lg); } .booking-rules-reviewed { font-size: 0.875rem; }`}</style>
    </form>
  );
}

const DELEGATED_APPROVER_KEY = 'approvals.delegated_approver_id';
/** Permissions that make someone a sensible stand-in approver. */
const APPROVER_PERMISSIONS = ['exceptions.approve_all', 'costs.approve_all', 'exceptions.approve', 'costs.approve'];

/** Picks an active staff login that can approve exceptions or costs; stores its user account id. */
function DelegatedApproverSelect({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { data, loading, error } = useAsync(async () => {
    const [people, roles] = await Promise.all([peopleApi.list(), rolesApi.list()]);
    const approverRoleIds = new Set(
      roles.filter((r) => APPROVER_PERMISSIONS.some((p) => r.permissions?.[p])).map((r) => r.id),
    );
    return people
      .filter(
        (p) =>
          p.status === 'active' &&
          p.userAccountId &&
          p.loginStatus === 'active' &&
          !p.clientId &&
          (p.systemRoles ?? []).some((r) => approverRoleIds.has(r.id)),
      )
      .map((p) => ({
        userId: p.userAccountId as string,
        name: p.fullName ?? p.name ?? p.loginEmail ?? 'Unnamed',
        roles: (p.systemRoles ?? []).filter((r) => approverRoleIds.has(r.id)).map((r) => r.name),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const options = data ?? [];
  const unknownCurrent = Boolean(value) && !loading && !options.some((o) => o.userId === value);

  return (
    <Select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={loading}
      hint={error ? `Could not load people: ${error}` : undefined}
      style={{ marginBottom: 0 }}
    >
      <option value="">{loading ? 'Loading…' : 'No delegated approver'}</option>
      {unknownCurrent && <option value={value}>Current choice (no longer eligible)</option>}
      {options.map((o) => (
        <option key={o.userId} value={o.userId}>
          {o.name} — {o.roles.join(', ')}
        </option>
      ))}
    </Select>
  );
}

function SettingsRegistryPanel() {
  const { data: rows, loading, error: loadError, reload } = useAsync(() => settingsApi.list(), []);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const { flash, ok, error, clear } = useFlash();

  const draftFor = (row: SettingRow) =>
    drafts[row.key] ?? (row.valueType === 'json' ? JSON.stringify(row.value) : String(row.value ?? ''));

  const save = async (row: SettingRow) => {
    setSavingKey(row.key);
    clear();
    const label = settingLabel(row.key);
    try {
      let value: unknown = draftFor(row);
      if (row.valueType === 'number') value = Number(value);
      else if (row.valueType === 'boolean') value = value === 'true';
      else if (row.valueType === 'json') {
        try {
          value = JSON.parse(String(value));
        } catch {
          error(`${label}: the value isn't in a valid format. Check brackets and quotes.`);
          return;
        }
      }

      await settingsApi.setValue(row.key, value);
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      ok(`${label} saved`);
      reload();
    } catch (err) {
      error(errorText(err, `Could not save ${label}.`));
    } finally {
      setSavingKey(null);
    }
  };

  const grouped = (rows ?? []).reduce<Record<string, SettingRow[]>>((acc, row) => {
    (acc[row.domain] ??= []).push(row);
    return acc;
  }, {});

  return (
    <section className="registry-panel">
      <h2>Advanced settings</h2>
      <p className="text-muted" style={{ marginTop: '-0.5rem' }}>
        Every adjustable rule in DispatchDash, grouped by area. Changes apply as soon as you save.
      </p>
      <FlashMessage flash={flash} />
      {loadError && !rows ? (
        <p className="text-urgent">
          Could not load settings. {loadError}{' '}
          <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Retry</Button>
        </p>
      ) : loading && !rows ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => settingDomainLabel(a).localeCompare(settingDomainLabel(b)))
          .map(([domain, domainRows]) => (
            <div key={domain} className="registry-domain">
              <h3>{settingDomainLabel(domain)}</h3>
              <table className="registry-table">
                <tbody>
                  {domainRows
                    .sort((a, b) => settingLabel(a.key).localeCompare(settingLabel(b.key)))
                    .map((row) => {
                      const meta = SETTING_META[row.key];
                      const inputId = `setting-${row.key.replace(/[^a-z0-9]+/gi, '-')}`;
                      const dirty = drafts[row.key] !== undefined;
                      return (
                        <tr key={row.key}>
                          <td className="registry-label">
                            <label htmlFor={inputId}>
                              {settingLabel(row.key)}
                              {meta?.unit && <span className="text-muted"> ({meta.unit})</span>}
                            </label>
                            {meta?.help && <span className="registry-help">{meta.help}</span>}
                            <code className="registry-key">{row.key}</code>
                          </td>
                          <td>
                            {row.valueType === 'boolean' ? (
                              <label className="registry-toggle">
                                <input
                                  id={inputId}
                                  type="checkbox"
                                  checked={draftFor(row) === 'true'}
                                  onChange={(e) =>
                                    setDrafts({ ...drafts, [row.key]: String(e.target.checked) })
                                  }
                                />
                                <span>{draftFor(row) === 'true' ? 'On' : 'Off'}</span>
                              </label>
                            ) : row.key === DELEGATED_APPROVER_KEY ? (
                              <DelegatedApproverSelect
                                id={inputId}
                                value={draftFor(row)}
                                onChange={(v) => setDrafts({ ...drafts, [row.key]: v })}
                              />
                            ) : row.valueType === 'json' ? (
                              <textarea
                                id={inputId}
                                className="registry-json"
                                rows={2}
                                value={draftFor(row)}
                                onChange={(e) => setDrafts({ ...drafts, [row.key]: e.target.value })}
                              />
                            ) : (
                              <Input
                                id={inputId}
                                type={row.valueType === 'number' || row.valueType === 'money' ? 'number' : 'text'}
                                inputMode={row.valueType === 'number' || row.valueType === 'money' ? 'decimal' : undefined}
                                step={row.valueType === 'number' || row.valueType === 'money' ? 'any' : undefined}
                                value={draftFor(row)}
                                onChange={(e) =>
                                  setDrafts({ ...drafts, [row.key]: e.target.value })
                                }
                                style={{ marginBottom: 0 }}
                              />
                            )}
                          </td>
                          <td>
                            <Button
                              type="button"
                              size="sm"
                              variant={dirty ? 'primary' : 'secondary'}
                              loading={savingKey === row.key}
                              disabled={!dirty}
                              onClick={() => void save(row)}
                            >
                              Save
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          ))
      )}
      <style>{`
        .registry-panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .registry-domain { margin-top: var(--space-lg); }
        .registry-domain h3 {
          font-size: 0.9375rem;
          color: var(--color-forest);
          margin-bottom: var(--space-sm);
        }
        .registry-table { width: 100%; border-collapse: collapse; }
        .registry-table td {
          padding: var(--space-xs) var(--space-sm);
          border-bottom: 1px solid var(--color-border);
          vertical-align: middle;
        }
        .registry-label { width: 45%; }
        .registry-label label { display: block; font-weight: 600; font-size: 0.875rem; }
        .registry-help { display: block; font-size: 0.8125rem; color: var(--color-text-muted); }
        .registry-key {
          display: block;
          font-family: var(--font-mono, monospace);
          font-size: 0.6875rem;
          color: var(--color-text-muted);
          opacity: 0.8;
        }
        .registry-toggle { display: inline-flex; align-items: center; gap: var(--space-sm); cursor: pointer; }
        .registry-toggle input { width: 1.125rem; height: 1.125rem; }
        .registry-json {
          width: 100%;
          font-family: var(--font-mono, monospace);
          font-size: 0.8125rem;
          padding: var(--space-xs) var(--space-sm);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          resize: vertical;
        }
        @media (max-width: 700px) {
          .registry-table, .registry-table tbody, .registry-table tr, .registry-table td { display: block; width: 100%; }
          .registry-table td { border-bottom: none; }
          .registry-table tr { border-bottom: 1px solid var(--color-border); padding-bottom: var(--space-sm); }
        }
      `}</style>
    </section>
  );
}

function RolesPanel() {
  const { data: roles, loading, reload } = useAsync(() => rolesApi.list(), []);
  const [selected, setSelected] = useState<Role | null>(null);
  const [permDraft, setPermDraft] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const { flash, ok, error, clear } = useFlash();
  const confirm = useConfirm();

  const openRole = (role: Role) => {
    setSelected(role);
    setPermDraft(role.permissions ?? {});
    clear();
  };

  const togglePerm = async (key: string) => {
    const removing = !!permDraft[key];
    if (removing && selected?.name === 'Admin' && LOCKOUT_PERMISSIONS.includes(key)) {
      const confirmed = await confirm({
        title: `Remove “${permissionLabel(key)}” from Admin?`,
        message:
          'If no other role has this permission, nobody will be able to change settings or roles, ' +
          'and you could lock yourself out. The change only takes effect when you click “Save permissions”.',
        confirmLabel: 'Remove permission',
        danger: true,
      });
      if (!confirmed) return;
    }
    setPermDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    clear();
    try {
      const updated = await rolesApi.updatePermissions(selected.id, permDraft);
      setSelected(updated);
      ok(`${selected.name} permissions saved`);
      reload();
    } catch (err) {
      error(errorText(err, 'Could not save permissions.'));
    } finally {
      setSaving(false);
    }
  };

  const groups = groupPermissions(Object.keys(permDraft));

  return (
    <section className="roles-panel">
      <div className="roles-layout">
        <aside className="role-list">
          <h2>Roles</h2>
          {loading ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : (
            (roles ?? []).map((role) => (
              <button
                key={role.id}
                type="button"
                className={`role-item ${selected?.id === role.id ? 'active' : ''}`}
                onClick={() => openRole(role)}
              >
                <strong>{role.name}</strong>
                {role.isSystem && <span className="text-muted">Built-in</span>}
              </button>
            ))
          )}
        </aside>

        <section className="role-detail">
          {selected ? (
            <>
              <h3>{selected.name} permissions</h3>
              <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
                Tick what people with this role can do. Anything unticked is not allowed.
              </p>
              <FlashMessage flash={flash} />
              {groups.map((group) => (
                <fieldset key={group.id} className="perm-group">
                  <legend>{group.heading}</legend>
                  <div className="perm-grid">
                    {group.keys.map((key) => (
                      <label key={key} className="perm-row">
                        <input
                          type="checkbox"
                          checked={!!permDraft[key]}
                          onChange={() => void togglePerm(key)}
                        />
                        <span>
                          {permissionLabel(key)}
                          <code className="perm-key">{key}</code>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
              {!groups.length && (
                <p className="text-muted">No permissions set on this role yet.</p>
              )}
              <Button type="button" loading={saving} onClick={() => void save()} style={{ marginTop: 'var(--space-md)' }}>
                Save permissions
              </Button>
            </>
          ) : (
            <p className="text-muted">Select a role to see and change what it can do.</p>
          )}
        </section>
      </div>

      <style>{`
        .roles-panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .roles-layout {
          display: grid;
          grid-template-columns: 220px 1fr;
          gap: var(--space-lg);
        }
        @media (max-width: 800px) {
          .roles-layout { grid-template-columns: 1fr; }
        }
        .role-list { display: flex; flex-direction: column; gap: 4px; }
        .role-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-sm) var(--space-md);
          border: none;
          border-radius: var(--radius-sm);
          background: transparent;
          cursor: pointer;
          text-align: left;
          font-size: 0.875rem;
        }
        .role-item:hover { background: var(--color-sand); }
        .role-item.active { background: var(--color-forest); color: var(--color-sand); }
        .perm-group {
          border: none;
          padding: 0;
          margin: 0 0 var(--space-md);
        }
        .perm-group legend {
          font-weight: 700;
          font-size: 0.875rem;
          color: var(--color-forest);
          margin-bottom: var(--space-xs);
        }
        .perm-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: var(--space-xs) var(--space-md);
        }
        .perm-row {
          display: flex;
          align-items: flex-start;
          gap: var(--space-sm);
          font-size: 0.8125rem;
          cursor: pointer;
        }
        .perm-row input { margin-top: 2px; }
        .perm-key {
          display: block;
          font-family: var(--font-mono, monospace);
          font-size: 0.6875rem;
          color: var(--color-text-muted);
        }
      `}</style>
    </section>
  );
}

const TIER_OPTIONS: NotificationTier[] = ['act_now', 'awareness', 'digest'];

const KNOWN_TEMPLATE_TYPES = [
  'trip.exception_filed',
  'trip.rtb_pending',
  'trip.quote_ready',
  'held_cargo.aged',
  'billing.push_failed',
  'portal.user_invite',
  'auth.password_reset',
  'auth.password_reset_link',
];

function NotificationsPanel() {
  const { data: templates, loading: templatesLoading, reload: reloadTemplates } = useAsync(
    () => notificationsApi.templates(),
    [],
  );
  const { data: log, loading: logLoading, reload: reloadLog } = useAsync(
    () => notificationsApi.deliveryLog(100),
    [],
  );

  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [form, setForm] = useState({
    type: '',
    subjectTemplate: '',
    bodyTemplate: '',
    defaultTier: 'awareness' as NotificationTier,
  });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const { flash, error, clear } = useFlash();
  const toast = useToast();

  const knownTypes = Array.from(
    new Set([...KNOWN_TEMPLATE_TYPES, ...(templates ?? []).map((t) => t.type)]),
  );

  const openNew = () => {
    setEditing(null);
    setForm({ type: '', subjectTemplate: '', bodyTemplate: '', defaultTier: 'awareness' });
    setShowForm(true);
    clear();
  };

  const openEdit = (t: NotificationTemplate) => {
    setEditing(t);
    setForm({
      type: t.type,
      subjectTemplate: t.subjectTemplate,
      bodyTemplate: t.bodyTemplate,
      defaultTier: t.defaultTier,
    });
    setShowForm(true);
    clear();
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clear();
    try {
      await notificationsApi.saveTemplate(form);
      setShowForm(false);
      toast(`Template “${notificationTypeLabel(form.type)}” saved`);
      reloadTemplates();
    } catch (err) {
      error(errorText(err, 'Could not save the template.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="notifications-panel">
      <div className="row-between" style={{ marginBottom: '1rem' }}>
        <div>
          <h2>Notification templates</h2>
          <p className="text-muted">
            Three levels of urgency — Act now (push, SMS and email, even during quiet hours), Awareness
            (push and email) and Daily digest (one email a day). Quiet hours hold back push and SMS for
            anything that isn't urgent.
          </p>
        </div>
        <Button size="sm" onClick={openNew}>New template</Button>
      </div>

      <DataTable
        data={templates ?? []}
        keyField="id"
        loading={templatesLoading}
        dense
        emptyMessage="No templates configured yet."
        columns={[
          { key: 'type', header: 'Notification', render: (r) => notificationTypeLabel(r.type) },
          {
            key: 'defaultTier',
            header: 'Default urgency',
            render: (r) => notificationTierLabel(r.defaultTier),
          },
          { key: 'subjectTemplate', header: 'Subject' },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
            ),
          },
        ]}
      />

      <div className="row-between" style={{ marginTop: '2rem', marginBottom: '1rem' }}>
        <div>
          <h3>Delivery log</h3>
          <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
            Recent notifications to everyone, and what happened on each channel (sent, held for quiet
            hours, or not sent because outgoing delivery is paused).
          </p>
        </div>
        <Button size="sm" variant="secondary" onClick={() => reloadLog()}>Refresh</Button>
      </div>

      <DataTable
        data={log ?? []}
        keyField="id"
        loading={logLoading}
        dense
        emptyMessage="No notifications sent yet."
        columns={[
          {
            key: 'createdAt',
            header: 'Sent',
            render: (r) => formatDateTime(r.createdAt),
          },
          { key: 'recipientEmail', header: 'Recipient' },
          {
            key: 'tier',
            header: 'Urgency',
            render: (r) => notificationTierLabel(r.tier),
          },
          { key: 'category', header: 'Notification', render: (r) => notificationTypeLabel(r.category) },
          { key: 'subject', header: 'Subject' },
          {
            key: 'deliveryResults',
            header: 'Channels',
            render: (r) => (
              <div className="gate-chip-row">
                {Object.entries(r.deliveryResults ?? {}).map(([ch, result]) => (
                  <span
                    key={ch}
                    className={`gate-chip ${result === 'deferred_quiet_hours' ? 'gate-chip--fail' : 'gate-chip--pass'}`}
                  >
                    {channelLabel(ch)}: {deliveryResultLabel(String(result))}
                  </span>
                ))}
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit template: ${notificationTypeLabel(editing.type)}` : 'New template'}
      >
        <form onSubmit={save}>
          {editing ? (
            <Input label="Notification" value={notificationTypeLabel(form.type)} disabled />
          ) : (
            <Select
              label="Notification"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              required
            >
              <option value="">Select a notification…</option>
              {knownTypes.map((t) => (
                <option key={t} value={t}>{notificationTypeLabel(t)}</option>
              ))}
            </Select>
          )}
          <Select
            label="Default urgency"
            value={form.defaultTier}
            onChange={(e) => setForm({ ...form, defaultTier: e.target.value as NotificationTier })}
          >
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>{notificationTierLabel(t)}</option>
            ))}
          </Select>
          <Input
            label="Subject template"
            value={form.subjectTemplate}
            onChange={(e) => setForm({ ...form, subjectTemplate: e.target.value })}
            placeholder="Exception filed on {{tripNumber}}"
            required
          />
          <label className="field-label">Body template</label>
          <textarea
            className="template-body-input"
            value={form.bodyTemplate}
            onChange={(e) => setForm({ ...form, bodyTemplate: e.target.value })}
            placeholder="An exception was filed on trip {{tripNumber}}. {{summary}}"
            rows={4}
            required
          />
          <p className="text-muted" style={{ fontSize: '0.75rem' }}>
            Use {'{{variableName}}'} placeholders — they're substituted when the notification is sent.
          </p>
          <FlashMessage flash={flash} />
          <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Create template'}</Button>
        </form>
        <style>{formStyles}</style>
      </Modal>

      <HardFloorsPanel />

      <style>{`
        .notifications-panel {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
        }
        .notifications-panel h2 { margin-bottom: var(--space-xs); }
        .notifications-panel h3 { font-size: 1rem; margin-bottom: var(--space-xs); }
        .field-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--color-text-muted);
        }
        .template-body-input {
          width: 100%;
          font-family: var(--font-body);
          font-size: 0.875rem;
          padding: var(--space-sm);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          resize: vertical;
        }
        .gate-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-xs);
        }
        .gate-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-weight: 600;
          white-space: nowrap;
        }
        .gate-chip--pass {
          background: rgba(46, 139, 87, 0.12);
          color: #2E8B57;
        }
        .gate-chip--fail {
          background: rgba(200, 60, 50, 0.12);
          color: #C83C32;
        }
      `}</style>
    </section>
  );
}

const FLOOR_CHANNELS: Array<{ key: string; label: string }> = [
  { key: 'push', label: 'Push' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
];

const QUIET_HOURS_POLICY_OPTIONS: Array<{ value: 'defer_interruptive' | 'always_deliver'; label: string }> = [
  { value: 'defer_interruptive', label: 'Defer interruptive channels during quiet hours' },
  { value: 'always_deliver', label: 'Always deliver, even during quiet hours' },
];

function HardFloorsPanel() {
  const { data: floors, loading, reload } = useAsync(() => notificationsApi.hardFloors(), []);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<HardFloor | null>(null);
  const [form, setForm] = useState({
    tier: 'act_now' as NotificationTier,
    category: '',
    channels: new Set<string>(),
    quietHoursPolicy: 'defer_interruptive' as 'defer_interruptive' | 'always_deliver',
    description: '',
  });
  const [saving, setSaving] = useState(false);
  const { flash, error, clear } = useFlash();
  const toast = useToast();
  const [probeBusy, setProbeBusy] = useState(false);
  const [probeResult, setProbeResult] = useState('');

  const openNew = () => {
    setEditing(null);
    setForm({
      tier: 'act_now',
      category: '',
      channels: new Set(),
      quietHoursPolicy: 'defer_interruptive',
      description: '',
    });
    setShowForm(true);
    clear();
  };

  const openEdit = (floor: HardFloor) => {
    setEditing(floor);
    setForm({
      tier: floor.tier,
      category: floor.category,
      channels: new Set(floor.channels),
      quietHoursPolicy: (floor.quietHoursPolicy as 'defer_interruptive' | 'always_deliver') ?? 'defer_interruptive',
      description: floor.description ?? '',
    });
    setShowForm(true);
    clear();
  };

  const toggleChannel = (key: string) => {
    setForm((prev) => {
      const next = new Set(prev.channels);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, channels: next };
    });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.channels.size) {
      error('Select at least one channel');
      return;
    }
    setSaving(true);
    clear();
    try {
      await notificationsApi.saveHardFloor({
        tier: form.tier,
        category: form.category.trim(),
        channels: Array.from(form.channels),
        quietHoursPolicy: form.quietHoursPolicy,
        description: form.description.trim() || undefined,
      });
      setShowForm(false);
      toast(`Always-deliver rule for “${notificationTypeLabel(form.category.trim())}” saved`);
      reload();
    } catch (err) {
      error(errorText(err, 'Could not save the rule.'));
    } finally {
      setSaving(false);
    }
  };

  const runProbe = async (floor?: HardFloor) => {
    const tier = floor?.tier ?? form.tier;
    const category = (floor?.category ?? form.category).trim();
    if (!category) {
      setProbeResult('Set a category (or select a hard floor) before probing');
      return;
    }
    setProbeBusy(true);
    setProbeResult('');
    try {
      const result = await notificationsApi.qaProbe({ tier, category });
      const d = result.diagnostics;
      setProbeResult(
        [
          'Test notification sent to your in-app inbox.',
          `Always-deliver rule: ${d.hardFloorMatched ? 'applies' : 'none'}.`,
          `Quiet hours now: ${d.inQuietHours ? 'yes' : 'no'}${d.deferQuietHours ? ' (push and SMS would be held)' : ''}.`,
          `Would go by: ${d.resolvedChannels.map(channelLabel).join(', ') || '—'}.`,
          'No email, SMS or push was actually sent.',
        ].join(' '),
      );
    } catch (err) {
      setProbeResult(errorText(err, 'Could not send the test notification.'));
    } finally {
      setProbeBusy(false);
    }
  };

  return (
    <section className="notifications-panel" style={{ marginTop: 'var(--space-xl)' }}>
      <div className="row-between" style={{ marginBottom: '1rem' }}>
        <div>
          <h2>Always-deliver rules</h2>
          <p className="text-muted">
            Business-critical notifications that always reach people on the chosen channels, even if
            they've turned that channel off. “Always deliver” also ignores their quiet hours.
          </p>
        </div>
        <div className="row" style={{ gap: '0.5rem' }}>
          <Button size="sm" variant="secondary" loading={probeBusy} onClick={() => runProbe()}>
            Send me a test
          </Button>
          <Button size="sm" onClick={openNew}>New rule</Button>
        </div>
      </div>
      {probeResult && <p className="text-muted" style={{ marginBottom: '1rem' }}>{probeResult}</p>}

      <DataTable
        data={floors ?? []}
        keyField="id"
        loading={loading}
        dense
        emptyMessage="No always-deliver rules — every notification follows its urgency level and each person's preferences."
        columns={[
          {
            key: 'tier',
            header: 'Urgency',
            render: (r) => notificationTierLabel(r.tier),
          },
          { key: 'category', header: 'Notification', render: (r) => notificationTypeLabel(r.category) },
          {
            key: 'channels',
            header: 'Guaranteed channels',
            render: (r) => (
              <div className="gate-chip-row">
                {r.channels.map((ch) => (
                  <span key={ch} className="gate-chip gate-chip--pass">{channelLabel(ch)}</span>
                ))}
              </div>
            ),
          },
          {
            key: 'quietHoursPolicy',
            header: 'Quiet hours',
            render: (r) => (r.quietHoursPolicy === 'always_deliver' ? 'Always deliver' : 'Defer interruptive'),
          },
          { key: 'description', header: 'Notes', render: (r) => r.description || '—' },
          {
            key: 'actions',
            header: '',
            render: (r) => (
              <div className="row" style={{ gap: '0.35rem' }}>
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                <Button size="sm" variant="secondary" loading={probeBusy} onClick={() => runProbe(r)}>
                  Test
                </Button>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit rule: ${notificationTypeLabel(editing.category)}` : 'New always-deliver rule'}
      >
        <form onSubmit={save}>
          <Select
            label="Urgency"
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value as NotificationTier })}
            disabled={!!editing}
          >
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>{notificationTierLabel(t)}</option>
            ))}
          </Select>
          <Select
            label="Notification"
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
            disabled={!!editing}
            required
          >
            <option value="">Select a notification…</option>
            {Array.from(new Set([...KNOWN_TEMPLATE_TYPES, ...(form.category ? [form.category] : [])])).map((t) => (
              <option key={t} value={t}>{notificationTypeLabel(t)}</option>
            ))}
          </Select>
          <label className="field-label">Guaranteed channels</label>
          <div className="floor-channel-row">
            {FLOOR_CHANNELS.map((ch) => (
              <label key={ch.key} className="perm-row">
                <input
                  type="checkbox"
                  checked={form.channels.has(ch.key)}
                  onChange={() => toggleChannel(ch.key)}
                />
                <span>{ch.label}</span>
              </label>
            ))}
          </div>
          <Select
            label="Quiet hours policy"
            value={form.quietHoursPolicy}
            onChange={(e) =>
              setForm({ ...form, quietHoursPolicy: e.target.value as 'defer_interruptive' | 'always_deliver' })
            }
          >
            {QUIET_HOURS_POLICY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
          <Input
            label="Notes (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Why this category is business-critical"
          />
          <FlashMessage flash={flash} />
          <Button type="submit" loading={saving}>{editing ? 'Save changes' : 'Create rule'}</Button>
        </form>
        <style>{formStyles}</style>
      </Modal>

      <style>{`
        .floor-channel-row {
          display: flex;
          gap: var(--space-lg);
          margin-bottom: var(--space-md);
        }
      `}</style>
    </section>
  );
}

const formStyles = `
  .admin-form {
    max-width: 560px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-xl);
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
  }
  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-md);
  }
`;
